import { SearchBar } from 'components/Atoms/SearchBar/SearchBar';
import { FeedItem } from 'types/feeds';
import { ContentFeedMetadata, ContentType } from 'types/publishedAssets';
import { useEffect, useMemo, useState } from 'react';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { Icon } from 'components/Atoms/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { useScreenSize } from 'utils/useScreenSize';
import { CustomerFeedParticipationCountsResponse } from 'src/service/engageFrontendGateway';
import { FeedItemCard } from './FeedItemCard';
import { matchesFeedKeyword } from './feedKeywordSearch';
import styles from './FeedItemsInTagGroup.module.scss';

type FeedItemsInTagGroupProps = {
  tag: string;
  items: FeedItem[];
  feedMetadata: ContentFeedMetadata;
  variant: 'grid' | 'list';
  isTwoColumns?: boolean;
  participationCounts: CustomerFeedParticipationCountsResponse;
  content: ContentType;
  /**
   * True when the block renders double-colon filters. The keyword search then lives in that
   * filter row, which applies it before the items reach this group, so the group renders no bar
   * of its own and filters nothing. Otherwise the group owns its own search bar.
   */
  isSearchHoisted?: boolean;
};
export function FeedItemsInTagGroup(props: FeedItemsInTagGroupProps) {
  const {
    tag,
    items,
    feedMetadata,
    variant,
    isTwoColumns,
    participationCounts,
    content,
    isSearchHoisted = false,
  } = props;
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { feed_type: feedType, number_of_items: numberOfItems } = feedMetadata;
  // Showing all items for SEO
  const [showAllItems, setShowAllItems] = useState(true);
  const [searchValue, setSearchValue] = useState('');

  const getDisplayClass = () => {
    if (isMobileView) return styles.feedList;
    if (isTwoColumns) return styles.feedList;

    return variant === 'list' ? styles.feedList : styles.feedGrid;
  };

  const displayMoreItemsButton = useMemo(
    () => numberOfItems !== 'all' && items.length > Number(numberOfItems),
    [numberOfItems, items],
  );

  if (!feedType) throw new Error('Missing feed type');

  const getActionButtonLabel = () => {
    switch (feedType) {
      case 'engagement_activity':
        return showAllItems ? t('feed.showLessActivities') : t('feed.showAllActivities');
      case 'project':
        return showAllItems ? t('feed.showLessProjects') : t('feed.showAllProjects');
      case 'update':
        return showAllItems ? t('feed.showLessUpdates') : t('feed.showAllUpdates');
      case 'hub':
        return showAllItems ? t('feed.showLessHubs') : t('feed.showAllHubs');
      default:
        return '';
    }
  };

  const getVariant = () => {
    if (isMobileView) return 'imageTop';

    return variant === 'grid' ? 'imageTop' : 'imageLeft';
  };

  const filteredItems = useMemo(
    () => items.filter((item) => matchesFeedKeyword(item, searchValue)),
    [items, searchValue],
  );

  const renderedFeedItems = useMemo(() => {
    if (showAllItems) return filteredItems;

    return filteredItems.slice(0, Number(numberOfItems));
  }, [filteredItems, showAllItems, numberOfItems]);

  const getParticipationCount = (item: FeedItem): number | undefined => {
    switch (item.type) {
      case 'Engagement':
        return participationCounts.engagements?.[item.id];
      case 'DiscussionTopic':
        return participationCounts.discussionTopics?.[item.id];
      case 'IdeaBoard':
        return participationCounts.ideaBoards?.[item.id];
      default:
        return 0;
    }
  };

  useEffect(() => {
    setShowAllItems(numberOfItems === 'all');
  }, [numberOfItems]);

  return (
    <div>
      {tag && <h3 className={styles.tagGroupTitle}>{tag}</h3>}
      {items.length > 6 ? (
        <>
          {!isSearchHoisted && (
            <SearchBar
              debounceTime={300}
              placeholder={t('feed.searchPlaceholder')}
              value={searchValue}
              onChange={setSearchValue}
              className={styles.searchBar}
            />
          )}
          <div className={styles.itemsCount}> {t('feed.itemsFound', { count: filteredItems.length })}</div>
        </>
      ) : null}
      <div className={[styles.feedItemsContainer, getDisplayClass()].join(' ')}>
        {renderedFeedItems.map((item) => (
          <FeedItemCard
            key={item.id}
            data={item}
            type={feedType}
            variant={getVariant()}
            isTwoColumns={isTwoColumns}
            participationCount={getParticipationCount(item)}
            content={content}
          />
        ))}
      </div>

      {displayMoreItemsButton && (
        <ActionButton
          className={styles.showMoreItemsButton}
          type={ButtonType.ACTION_SECONDARY}
          onClick={() => setShowAllItems(!showAllItems)}
        >
          {getActionButtonLabel()}
          <Icon name={showAllItems ? 'chevron-up' : 'chevron-down'} />
        </ActionButton>
      )}
    </div>
  );
}
